import { useBoundContext } from "lib/@components/binding/use-bound-context"
import {
    darken,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
} from "@mui/material"
import { ListItemBox } from "lib/@components/ListItemBox"
import { FitInRemainingSpace } from "lib/@components/fit-in-remaining-space"
import { MemorizedPagedRepeat } from "lib/@components/memorized-paged-repeat"
import { Frag } from "lib/@components/slot/frag"
import { ScheduleProfile } from "routes/asset-profile/schedule-profile"
import { If } from "lib/@components/switch"
import { Repeat } from "lib/@components/repeat"
import { RegimeNavigator } from "routes/asset-register/plugins/regime-display/regime-navigator"
import { NotInPdf } from "lib/@components/pdf/not-in-pdf"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { PaginationPortal } from "lib/@components/pagination-portal"
import {
    getAssetProfileTableStats,
    PROFILE_SCOPE_SEARCH_PARAM,
    PROFILE_SCOPE_WITH_CHILDREN,
} from "routes/asset-profile/asset-profile-scope"
import { AssetProfileScopeToggle } from "routes/asset-profile/asset-profile-scope-toggle"

export function AssetProfileDetails() {
    const { isPDF, stats } = useBoundContext()

    const theme = useTheme()
    const [search] = useSearchParam("search", "", (v) => v.toLowerCase())
    const [scope] = useSearchParam(PROFILE_SCOPE_SEARCH_PARAM, PROFILE_SCOPE_WITH_CHILDREN)
    const tableStats = getAssetProfileTableStats(stats, scope)

    return (
        <TableContainer sx={{ mt: 0.5 }} component={Paper}>
            <NotInPdf>
                <ListItemBox sx={{ gap: 1.5, px: 1 }}>
                    <RegimeNavigator noButtons sx={{ width: "initial", flexShrink: 0 }} />
                    <AssetProfileScopeToggle />
                    <SearchBoxUsingSearchParam
                        autoFocus
                        sx={{
                            width: 1,
                            py: 0,
                            "& .MuiInputBase-root": { fontSize: 13 },
                            "& .MuiInputBase-input": { fontSize: 13, lineHeight: 1.4 },
                            "& .MuiInputBase-input::placeholder": { fontSize: 13 },
                        }}
                        placeholder="Search..."
                    />
                    <PaginationPortal flexShrink={0} sx={{ whiteSpace: "nowrap" }} />
                </ListItemBox>
            </NotInPdf>
            <FitInRemainingSpace leaveSpace={12} sx={{ position: "relative" }}>
                <Table stickyHeader={true} size="small" data-cy="regime-asset-overview-table">
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    zIndex: 101,
                                    position: "sticky",
                                    left: 0,
                                    top: 0,
                                    backdropFilter: "blur(20px)",
                                    backgroundColor: darken(theme.palette.background.paper, 0.03),
                                    minWidth: "fit-content(30em)",
                                }}
                                data-cy="header-schedule-overview"
                            >
                                Schedule
                            </TableCell>
                            {tableStats.periods.map((period) => (
                                <TableCell
                                    sx={{ textAlign: "center", backdropFilter: "blur(20px)", minWidth: 90 }}
                                    key={period.frequency}
                                    data-cy={`header-${period.frequency}-overview`}
                                >
                                    {period.frequency}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ whiteSpace: "nowrap" }}>
                        <If
                            truthy={isPDF}
                            else={
                                <MemorizedPagedRepeat
                                    Component={Frag}
                                    pass="scheduleStats"
                                    list={structuredClone(tableStats.all).filter(searchList).sortBy(["where", "title"])}
                                    item={<ScheduleProfile stats={tableStats} />}
                                />
                            }
                            then={
                                <Repeat
                                    Component={Frag}
                                    pass="scheduleStats"
                                    list={structuredClone(tableStats.all).filter(searchList).sortBy(["where", "title"])}
                                    item={<ScheduleProfile stats={tableStats} />}
                                />
                            }
                        />
                    </TableBody>
                </Table>
            </FitInRemainingSpace>
        </TableContainer>
    )

    function searchList(item) {
        if (!search) return true

        return (
            item.title?.toLowerCase().includes(search) ||
            item.code?.toLowerCase().includes(search) ||
            item.where?.toLowerCase().includes(search)
        )
    }
}
